import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import rfdc from 'rfdc';
import { FadingTooltip } from '@cosmotech/ui';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import { TwoActionsDialogService } from '../../../../services/twoActionsDialog/twoActionsDialogService';
import { WizardUtils } from '../../../../utils/WizardUtils';
import { CriteriaFormulaEditor } from '../../components/CriteriaFormulaEditor';
import EditableTable from '../../components/EditableTable';
import GenericAutoCompleteInput from '../../components/GenericAutoCompleteInput';
import GenericMultiSelect from '../../components/GenericMultiSelect';
import { useReglesGestion } from './hooks/ReglesGestionHook';

const clone = rfdc();

const generateUniqueId = (type) => {
  const randomString = Math.random().toString(36).substring(2, 15);
  return `${type}-${randomString}`;
};

const ReglesGestionTab = ({
  selectedRegle,
  setSelectedRegle,
  selectedGisement,
  editMode,
  selectedAssetClassAttrsList,
  assetClassAttributesValues,
}) => {
  const {
    reglesGestion,
    criteresDeclenchement,
    status,
    niveauDePriorisation,
    tableNamesMapping,
    familleEquipement,
    sequenceActionsOptions,
    categoriesBudgetaires,
    referentielConsequences,
  } = useReglesGestion(selectedGisement);
  const { t } = useTranslation();

  const [regleOptions, setRegleOptions] = useState(
    WizardUtils.sortResourceListAlphabetically(reglesGestion?.map((regle) => regle.Identifiant))
  );

  // TODO: refactor this ugly work-around by either watching the form values or rewriting the component to have only
  // one controller?
  // Temporary work-around: this local state is only used to keep track of changes in the criteria table and reflect
  // its changes in the  criteria formula editor component
  const [localStateForCriteria, setLocalStateForCriteria] = useState(criteresDeclenchement);
  useEffect(() => {
    setLocalStateForCriteria(criteresDeclenchement);
  }, [criteresDeclenchement]);

  const { setValue, getValues } = useFormContext();

  const handleAddRegle = (newRegle) => {
    setSelectedRegle(newRegle);
    setRegleOptions((prevOptions) => [...(prevOptions ?? []), newRegle]);

    const newRule = {
      Identifiant: newRegle,
      TypePriorisation: 'Operational',
      NiveauDePriorisation: niveauDePriorisation[0] || '',
      AnneeDebut: new Date().getFullYear(),
      AnneeFin: new Date().getFullYear() + 10,
      CategoriesBudgetaires: '',
      SequenceActions1: sequenceActionsOptions?.[0] || '',
      SequenceActions2: '',
      NbIterationSequenceActions1: '',
      ReevaluationAnnuelleCriteres: false,
      Desactiver: false,
      FormuleCritere: 'C1',
    };

    const currentRules = getValues(tableNamesMapping.ReglesGestion) || [];
    setValue(tableNamesMapping.ReglesGestion, [...currentRules, newRule]);

    const newCriteriaId = 'C1';

    const newCriteria = {
      id: generateUniqueId('c'),
      RegleGestion: newRegle,
      Identifiant: newCriteriaId,
      SeuilValidation: 0,
      AttributDeclencheur: 'Failure',
      Operateur: '',
      ValeurReference: '',
      FamillesEquipements: '',
    };

    const currentCriteria = getValues(tableNamesMapping.CriteresDeclenchement) || [];
    setLocalStateForCriteria((prev) => [...(prev ?? []), newCriteria]);
    setValue(tableNamesMapping.CriteresDeclenchement, [...currentCriteria, newCriteria]);
  };

  const nonComparingAttributesList = ['Failure', 'QuasiFailure', 'Operating', 'AllPredeterminedProjectsDone'];

  // Function to check if the AttributDeclencheur is in the isOperateurDisabledForAttribut list
  const isOperateurDisabledForAttribut = useCallback((attributDeclencheur, nonComparingAttributes) => {
    return nonComparingAttributes.includes(attributDeclencheur) || attributDeclencheur.startsWith('CustomDataString.');
  }, []);

  // Function to check if the AttributDeclencheur is in the isValeurReferenceDisabled list
  const isValeurReferenceDisabled = useCallback((attributDeclencheur, nonComparingAttributes) => {
    return nonComparingAttributes.includes(attributDeclencheur);
  }, []);

  const isUniqueCriteriaIdentifiant = useCallback(
    (row) => {
      const criteriaForSameRule = localStateForCriteria.filter(
        (criteria) => criteria.RegleGestion === row.RegleGestion
      );
      return criteriaForSameRule.some((criteria) => criteria.Identifiant === row.Identifiant && criteria.id !== row.id);
    },
    [localStateForCriteria]
  );

  const setAssetClassAttributesValues = useCallback(
    (row) => {
      return assetClassAttributesValues[row.AttributDeclencheur] || {};
    },
    [assetClassAttributesValues]
  );

  const columns = TECHNICAL_POLICY_COLUMNS.CriteresDeclenchement(
    (row) => isOperateurDisabledForAttribut(row.AttributDeclencheur, nonComparingAttributesList),
    (row) => isValeurReferenceDisabled(row.AttributDeclencheur, nonComparingAttributesList),
    (row) => isUniqueCriteriaIdentifiant(row),
    (row) => setAssetClassAttributesValues(row),
    WizardUtils.sortResourceListAlphabetically(familleEquipement.map((famille) => famille.label))
  );

  const sortedAttributDeclencheurOptions = useMemo(() => {
    const attributDeclencheurColumn = columns.find((col) => col.field === 'AttributDeclencheur');
    if (!attributDeclencheurColumn?.options) return [];

    return [...attributDeclencheurColumn.options].sort((a, b) => {
      const hasAsteriskA = a.label?.endsWith('*') || false;
      const hasAsteriskB = b.label?.endsWith('*') || false;

      const getGroupPriority = (label) => {
        const cleanLabel = label?.replace('*', '') || '';
        if (cleanLabel.startsWith('CustomData')) {
          return 3;
        }

        if (cleanLabel.startsWith('ConsequenceQuantity') || cleanLabel.startsWith('SumOfConsequence')) {
          return 4;
        }

        return hasAsteriskA || hasAsteriskB ? 2 : 0;
      };

      const groupA = getGroupPriority(a.label);
      const groupB = getGroupPriority(b.label);

      if (groupA !== groupB) {
        return groupA - groupB;
      }

      if (hasAsteriskA !== hasAsteriskB) {
        return hasAsteriskA ? 1 : -1;
      }

      const labelA = hasAsteriskA ? (a.label || '').slice(0, -1) : a.label || '';
      const labelB = hasAsteriskB ? (b.label || '').slice(0, -1) : b.label || '';

      return labelA.localeCompare(labelB);
    });
  }, [columns]);

  // Update the original options with the sorted ones
  const attributDeclencheurColumn = columns.find((col) => col.field === 'AttributDeclencheur');
  if (attributDeclencheurColumn) {
    attributDeclencheurColumn.options = sortedAttributDeclencheurOptions;
  }

  const calculateNewValue = (value, changedValue, field) => {
    if (!value.map((rg) => rg.Identifiant).includes(selectedRegle)) {
      return [...value, { Identifiant: selectedRegle, [field]: changedValue }];
    }
    return value.map((regle) => {
      if (regle.Identifiant === selectedRegle) return { ...regle, [field]: changedValue };
      return regle;
    });
  };

  const handleDeleteRegleGestion = async (event) => {
    if (selectedRegle) {
      event.stopPropagation();
      const dialogProps = {
        id: 'delete-regle-gestion',
        labels: {
          title: t('genericcomponent.common.confirmDialogs.deleteManagementRule.title', 'Delete management rule?'),
          body: t(
            'genericcomponent.common.confirmDialogs.deleteManagementRule.body',
            'Trigger criteria associated with this rule will also be deleted.'
          ),
          button1: t('genericcomponent.common.cancel', 'CANCEL'),
          button2: t('genericcomponent.common.delete', 'DELETE'),
        },
      };
      const result = await TwoActionsDialogService.openDialog(dialogProps);
      if (result === 2) setSelectedRegle('');
      return result;
    }
  };

  if (status === 'LOADING_GISEMENT_DATA')
    return <CircularProgress data-cy="selected-gisement-queries-loading-spinner" />;

  return (
    <Box width={'100%'} sx={{ margin: '0 25px' }}>
      <Controller
        name={tableNamesMapping.ReglesGestion}
        defaultValue={reglesGestion ?? []}
        render={({ field }) => {
          const { value, onChange } = field;
          return (
            <>
              <Grid container sx={{ gap: 1 }}>
                <Grid item>
                  <GenericAutoCompleteInput
                    id="select-or-create-rule-autocomplete"
                    onChange={(e, newRegle) => {
                      setSelectedRegle(newRegle || '');
                      if (newRegle && !regleOptions?.includes(newRegle)) {
                        handleAddRegle(newRegle);
                      }
                    }}
                    onNewOption={(newRegle) => {
                      setSelectedRegle(newRegle || '');
                    }}
                    value={selectedRegle || ''}
                    options={regleOptions ?? []}
                    label={t(
                      'genericcomponent.technicalPolicy.managementRules.selectOrCreateRule',
                      'Select or create a management rule'
                    )}
                    editMode={editMode}
                  />
                </Grid>
                <Grid item>
                  {selectedRegle && editMode && (
                    <IconButton
                      data-cy="delete-rule-button"
                      onClick={async (event) => {
                        const response = await handleDeleteRegleGestion(event);
                        if (response === 2) {
                          const filteredValue = value.filter((rg) => rg.Identifiant !== selectedRegle);
                          onChange(filteredValue);

                          setRegleOptions(filteredValue.map((rg) => rg.Identifiant));
                          const critDValue = getValues(tableNamesMapping.CriteresDeclenchement);
                          setValue(
                            tableNamesMapping.CriteresDeclenchement,
                            critDValue
                              ? critDValue.filter((row) => row.RegleGestion !== selectedRegle)
                              : criteresDeclenchement.filter((row) => row.RegleGestion !== selectedRegle)
                          );
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>

                <Grid item sx={{ flexGrow: 1 }}></Grid>
              </Grid>
              <Grid container spacing={2} marginTop={2}>
                <Grid item xs={6}>
                  <FormControl sx={{ minWidth: '100%' }}>
                    <InputLabel id="type-priorisation" required>
                      {t('genericcomponent.technicalPolicy.managementRules.prioritizationType', 'Prioritization type')}
                    </InputLabel>
                    <Select
                      data-cy="priorisation-type"
                      disabled={!editMode || !selectedRegle}
                      label={t(
                        'genericcomponent.technicalPolicy.managementRules.prioritizationType',
                        'Prioritization type'
                      )}
                      labelId="type-priorisation"
                      value={value?.find((regle) => regle.Identifiant === selectedRegle)?.TypePriorisation || ''}
                      onChange={(e) => {
                        const changedValue = e.target.value;
                        onChange(calculateNewValue(value, changedValue, 'TypePriorisation'));
                      }}
                      fullWidth
                    >
                      <MenuItem data-cy="operational-priorisation-type" key={'operational'} value={'Operational'}>
                        Operational
                      </MenuItem>
                      <MenuItem data-cy="tactical-priorisation-type" key={'tactical'} value={'Tactical'}>
                        Tactical
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl sx={{ minWidth: '100%' }}>
                    <InputLabel id="niveau-priorisation" shrink={true} required>
                      {t(
                        'genericcomponent.technicalPolicy.managementRules.prioritizationLevel',
                        'Prioritization level'
                      )}
                    </InputLabel>
                    <Select
                      data-cy="priorisation-level"
                      label={t(
                        'genericcomponent.technicalPolicy.managementRules.prioritizationLevel',
                        'Prioritization level'
                      )}
                      labelId="niveau-priorisation"
                      displayEmpty={true}
                      disabled={!editMode || !selectedRegle}
                      value={value?.find((regle) => regle.Identifiant === selectedRegle)?.NiveauDePriorisation || ''}
                      renderValue={(selected) => {
                        return selected || 'Aucun';
                      }}
                      onChange={(e) => {
                        const changedValue = e.target.value;
                        onChange(calculateNewValue(value, changedValue, 'NiveauDePriorisation'));
                      }}
                      fullWidth
                    >
                      {niveauDePriorisation.length > 0 ? (
                        niveauDePriorisation.map((np) => (
                          <MenuItem data-cy={`${np}-priorisation-level`} key={np} value={np}>
                            {np}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem key="none" value="">
                          Aucun
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('genericcomponent.technicalPolicy.managementRules.startYear', 'Start Year')}
                    type="number"
                    disabled={!editMode || !selectedRegle}
                    value={value?.find((regle) => regle.Identifiant === selectedRegle)?.AnneeDebut || ''}
                    onChange={(e) => {
                      const changedValue = e.target.value;
                      onChange(calculateNewValue(value, changedValue, 'AnneeDebut'));
                    }}
                    fullWidth
                    inputProps={{ 'data-cy': 'rule-start-year', min: 1900, max: 5000 }}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('genericcomponent.technicalPolicy.managementRules.endYear', 'End Year')}
                    type="number"
                    disabled={!editMode || !selectedRegle}
                    value={value?.find((regle) => regle.Identifiant === selectedRegle)?.AnneeFin || ''}
                    onChange={(e) => {
                      const changedValue = e.target.value;
                      onChange(calculateNewValue(value, changedValue, 'AnneeFin'));
                    }}
                    fullWidth
                    inputProps={{ 'data-cy': 'rule-end-year', min: 1900, max: 5000 }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <GenericMultiSelect
                    disabled={!editMode || !selectedRegle}
                    dataCy="budget-categories"
                    options={categoriesBudgetaires}
                    value={[
                      ...new Set(
                        value
                          ?.find((regle) => regle.Identifiant === selectedRegle)
                          ?.CategoriesBudgetaires?.split('|')
                          .filter(Boolean) || []
                      ),
                    ]}
                    onChange={(_, newValue) => {
                      const changedValue = [...new Set(newValue.filter(Boolean))].join('|');
                      onChange(calculateNewValue(value, changedValue, 'CategoriesBudgetaires'));
                    }}
                    label={t('genericcomponent.technicalPolicy.managementRules.budgetCategories', 'Budget Categories')}
                  />
                </Grid>
                <Grid container spacing={2} width={'100%'} margin={'auto'}>
                  <Grid item xs={5}>
                    <FormControl sx={{ minWidth: '100%' }}>
                      <InputLabel id="action-principale" required>
                        {t(
                          'genericcomponent.technicalPolicy.managementRules.mainActionSequence',
                          'Main Action Sequence'
                        )}
                      </InputLabel>
                      <Select
                        data-cy="main-action-sequence"
                        label={t(
                          'genericcomponent.technicalPolicy.managementRules.mainActionSequence',
                          'Main Action Sequence'
                        )}
                        labelId="action-principale"
                        disabled={!editMode || !selectedRegle}
                        value={value?.find((regle) => regle.Identifiant === selectedRegle)?.SequenceActions1 || ''}
                        onChange={(e) => {
                          const changedValue = e.target.value;
                          onChange(calculateNewValue(value, changedValue, 'SequenceActions1'));
                        }}
                        fullWidth
                      >
                        {sequenceActionsOptions.map((sa) => (
                          <MenuItem data-cy={`${sa}-main-action`} key={sa} value={sa}>
                            {sa}
                          </MenuItem>
                        ))}
                        {/* Fallback if the value isn't in options */}
                        {(() => {
                          const currentValue = value?.find(
                            (regle) => regle.Identifiant === selectedRegle
                          )?.SequenceActions1;
                          if (currentValue && !sequenceActionsOptions.includes(currentValue)) {
                            return (
                              <MenuItem data-cy={`${currentValue}-main-action`} key={currentValue} value={currentValue}>
                                {currentValue} (supprimée)
                              </MenuItem>
                            );
                          }
                          return null;
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={5}>
                    <FormControl sx={{ minWidth: '100%' }}>
                      <InputLabel id="action-secondaire">
                        {t(
                          'genericcomponent.technicalPolicy.managementRules.secondaryActionSequence',
                          'Secondary Action Sequence'
                        )}
                      </InputLabel>
                      <Select
                        data-cy="secondary-action-sequence"
                        label={t(
                          'genericcomponent.technicalPolicy.managementRules.secondaryActionSequence',
                          'Secondary Action Sequence'
                        )}
                        labelId="action-secondaire"
                        disabled={!editMode || !selectedRegle}
                        value={value?.find((regle) => regle.Identifiant === selectedRegle)?.SequenceActions2 || ''}
                        onChange={(e) => {
                          const changedValue = e.target.value;
                          const currentRegle = value.find((regle) => regle.Identifiant === selectedRegle);

                          let updatedValue = calculateNewValue(value, changedValue, 'SequenceActions2');

                          // handle NbIterationSequenceActions1
                          if (changedValue) {
                            if (currentRegle && !currentRegle.NbIterationSequenceActions1) {
                              updatedValue = calculateNewValue(updatedValue, 1, 'NbIterationSequenceActions1');
                            }
                          } else {
                            // when clearing SequenceAction2
                            updatedValue = calculateNewValue(updatedValue, undefined, 'NbIterationSequenceActions1');
                          }

                          onChange(updatedValue);
                        }}
                        fullWidth
                      >
                        <MenuItem data-cy="secondary-action-" key="none" value="">
                          Aucune
                        </MenuItem>
                        {sequenceActionsOptions.map((sa) => (
                          <MenuItem data-cy={`${sa}-secondary-action`} key={sa} value={sa}>
                            {sa}
                          </MenuItem>
                        ))}
                        {/* Fallback if the value isn't in options */}
                        {(() => {
                          const currentValue = value?.find(
                            (regle) => regle.Identifiant === selectedRegle
                          )?.SequenceActions2;
                          if (currentValue && !sequenceActionsOptions.includes(currentValue)) {
                            return (
                              <MenuItem
                                data-cy={`${currentValue}-secondary-action`}
                                key={currentValue}
                                value={currentValue}
                              >
                                {currentValue} (supprimée)
                              </MenuItem>
                            );
                          }
                          return null;
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <FadingTooltip
                      title={t(
                        'genericcomponent.technicalPolicy.managementRules.numberOfIterations',
                        'Number of iterations'
                      )}
                    >
                      <TextField
                        label={t(
                          'genericcomponent.technicalPolicy.managementRules.numberOfIterations',
                          'Number of iterations'
                        )}
                        type={'number'}
                        disabled={
                          !editMode ||
                          !selectedRegle ||
                          !value?.find((regle) => regle.Identifiant === selectedRegle)?.SequenceActions2
                        }
                        value={
                          value?.find((regle) => regle.Identifiant === selectedRegle)?.NbIterationSequenceActions1 || 1
                        }
                        onChange={(e) => {
                          let changedValue = e.target.value;
                          changedValue = parseInt(changedValue);
                          onChange(
                            calculateNewValue(
                              value,
                              !isNaN(changedValue) ? changedValue : 1,
                              'NbIterationSequenceActions1'
                            )
                          );
                        }}
                        fullWidth
                        inputProps={{ 'data-cy': 'main-sequence-iterations', 'aria-valuemin': 1, min: 1 }}
                      />
                    </FadingTooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={WizardUtils.isValueTruthy(
                            value?.find((regle) => regle.Identifiant === selectedRegle)?.ReevaluationAnnuelleCriteres
                          )}
                          onChange={(e) => {
                            const changedValue = e.target.checked;
                            onChange(calculateNewValue(value, changedValue, 'ReevaluationAnnuelleCriteres'));
                          }}
                          disabled={!editMode || !selectedRegle}
                          inputProps={{ 'data-cy': 'annual-reevalutaion' }}
                        />
                      }
                      label={t(
                        'genericcomponent.technicalPolicy.managementRules.annualCriteriaReassessment',
                        'Main Action Sequence Annual Criteria Reassessment'
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={WizardUtils.isValueTruthy(
                            value?.find((regle) => regle.Identifiant === selectedRegle)?.Desactiver
                          )}
                          onChange={(e) => {
                            const changedValue = e.target.checked;
                            onChange(calculateNewValue(value, changedValue, 'Desactiver'));
                          }}
                          disabled={!editMode || !selectedRegle}
                          inputProps={{ 'data-cy': 'deactivate-rule' }}
                        />
                      }
                      label={t('genericcomponent.technicalPolicy.managementRules.deactivate', 'Deactivate')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          );
        }}
      />

      <Controller
        name={tableNamesMapping.CriteresDeclenchement}
        defaultValue={criteresDeclenchement ?? []}
        render={({ field }) => {
          const { value: allCriteria, onChange: setAllCriteria } = field;
          const criteriaForSelectedRule =
            selectedRegle != null
              ? (allCriteria
                  ?.filter((criterion) => criterion.RegleGestion === selectedRegle)
                  .sort((a, b) => a.Identifiant.localeCompare(b.Identifiant)) ?? [])
              : [];
          const handleChange = (newValue) => {
            setAllCriteria(newValue);
            setLocalStateForCriteria(newValue); // Update local state
          };

          const onNewRowSaved = (newRow) => {
            const isFirstCriterion = criteriaForSelectedRule.length === 0;
            if (!isFirstCriterion) return;

            const managementRules = getValues(tableNamesMapping.ReglesGestion);
            const criteriaFormula =
              managementRules.find((rule) => rule.Identifiant === selectedRegle)?.FormuleCritere ?? '';
            if (!WizardUtils.isFormulaEmpty(criteriaFormula)) return;

            const newFormula = `(${newRow.Identifiant})`;
            const newRules = managementRules.map((rule) => {
              if (rule.Identifiant !== selectedRegle) return rule;
              return { ...rule, FormuleCritere: newFormula };
            });
            setValue(tableNamesMapping.ReglesGestion, newRules);
          };

          const onDeleteRow = (deletedRow) => {
            const deletedCriterionId = deletedRow.Identifiant;
            if (criteriaForSelectedRule.filter((criterion) => criterion.Identifiant === deletedCriterionId).length > 1)
              return; // A duplicate entry was removed from the table, skip the update of the criteria formula

            const managementRules = getValues(tableNamesMapping.ReglesGestion);
            const criteriaFormula =
              managementRules.find((rule) => rule.Identifiant === selectedRegle)?.FormuleCritere ?? '';

            // Searching in the whole formula string might return false positives but it's faster than parsing the
            // condition string to then look in the generated condition tree; the function called below won't change the
            // formula if the detection was a false positive
            if (criteriaFormula.includes(deletedCriterionId)) {
              const newFormula = WizardUtils.removeCriterionIdFromConditionString(criteriaFormula, deletedCriterionId);
              const newRules = managementRules.map((rule) => {
                if (rule.Identifiant !== selectedRegle) return rule;
                return { ...rule, FormuleCritere: newFormula };
              });
              setValue(tableNamesMapping.ReglesGestion, newRules);
            }
          };

          const onRowUpdate = (newRow, previousRow) => {
            const previousCriterionId = previousRow.Identifiant;
            const newCriterionId = newRow.Identifiant;

            const managementRules = getValues(tableNamesMapping.ReglesGestion);
            const criteriaFormula =
              managementRules.find((rule) => rule.Identifiant === selectedRegle)?.FormuleCritere ?? '';

            // Searching in the whole formula string might return false positives but it's faster than parsing the
            // condition string to then look in the generated condition tree; the function called below won't change the
            // formula if the detection was a false positive
            if (criteriaFormula.includes(previousCriterionId)) {
              const newFormula = WizardUtils.renameCriterionInConditionString(
                criteriaFormula,
                previousCriterionId,
                newCriterionId
              );
              const newRules = managementRules.map((rule) => {
                if (rule.Identifiant !== selectedRegle) return rule;
                return { ...rule, FormuleCritere: newFormula };
              });
              setValue(tableNamesMapping.ReglesGestion, newRules);
            }
          };

          return (
            <>
              <Box sx={{ marginTop: 4 }}>
                <Typography variant="h6">
                  {t('genericcomponent.technicalPolicy.managementRules.triggerCriteria.title', 'Trigger Criteria')}
                </Typography>
                <EditableTable
                  id="triggering-criteria"
                  columns={columns.filter((col) => col.isInvisible !== true)}
                  rows={criteriaForSelectedRule}
                  editMode={editMode && !!selectedRegle}
                  onChange={handleChange}
                  notFilteredRows={allCriteria}
                  tripleInput={true}
                  addButtonAlignment="left"
                  selectedAssetClassAttrsList={selectedAssetClassAttrsList}
                  filteringCol={{ field: 'RegleGestion', value: selectedRegle }}
                  referentielConsequences={referentielConsequences}
                  onNewRowSaved={onNewRowSaved}
                  onDeleteRow={onDeleteRow}
                  onRowUpdate={onRowUpdate}
                />
              </Box>
            </>
          );
        }}
      />

      {selectedRegle && (
        <Controller
          name={tableNamesMapping.ReglesGestion}
          defaultValue={reglesGestion ?? []}
          render={({ field }) => {
            const { value, onChange } = field;
            const criteriaFormula = value.find((regle) => regle.Identifiant === selectedRegle)?.FormuleCritere ?? '';
            const setCriteriaFormula = (newCriteriaFormula) => {
              const newManagementRules = clone(value);
              const selectedRuleData = newManagementRules.find((rule) => rule.Identifiant === selectedRegle);

              if (selectedRuleData != null) selectedRuleData.FormuleCritere = newCriteriaFormula;
              else newManagementRules.push({ Identifiant: selectedRegle, FormuleCritere: '' });

              onChange(newManagementRules);
            };

            const criteriaForSelectedRule =
              selectedRegle != null
                ? (localStateForCriteria
                    ?.filter((criterion) => criterion.RegleGestion === selectedRegle)
                    .sort((a, b) => a.Identifiant.localeCompare(b.Identifiant)) ?? [])
                : [];

            return (
              <Box sx={{ marginTop: 4 }} data-cy="trigger-condition-container">
                <Typography variant="h6">
                  {t('genericcomponent.technicalPolicy.managementRules.triggerCondition', 'Trigger Condition')}
                </Typography>
                <CriteriaFormulaEditor
                  criteria={criteriaForSelectedRule}
                  value={criteriaFormula}
                  onChange={setCriteriaFormula}
                />
              </Box>
            );
          }}
        />
      )}
    </Box>
  );
};

ReglesGestionTab.propTypes = {
  selectedRegle: PropTypes.string,
  setSelectedRegle: PropTypes.func.isRequired,
  selectedGisement: PropTypes.string.isRequired,
  editMode: PropTypes.bool,
  selectedAssetClassAttrsList: PropTypes.array,
  assetClassAttributesValues: PropTypes.object,
};

ReglesGestionTab.defaultProps = {
  editMode: true,
};

export default ReglesGestionTab;
